/**
 * Aylık Kâr/Zarar Raporu — Sprint 32 (ADR-014).
 *
 * Gelir + tüm giderler (maaş, elektrik, su, tedarikçi) + zayiat = net kâr.
 * Günlük breakdown tablosu + ay özet KPI'ları.
 */
import {
  Button, Card, Divider, Group, Loader, Paper, Select, SimpleGrid,
  Stack, Table, Text, Title,
} from "@mantine/core";
import { AreaChart } from "@mantine/charts";
import { IconDownload } from "@tabler/icons-react";
import { useState } from "react";
import { downloadPLSummaryCsv, useMonthlyPL } from "../../../shared/api";
import { MoneyDisplay, PageHeader } from "../../../shared/ui";

/** ``year``/``month`` → ay başı ve sonu ``YYYY-MM-DD`` (CSV export için). */
function monthBounds(year: number, month: number): [string, string] {
  const pad = (n: number) => `${n}`.padStart(2, "0");
  const lastDay = new Date(year, month, 0).getDate();
  return [`${year}-${pad(month)}-01`, `${year}-${pad(month)}-${pad(lastDay)}`];
}

const TR_MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

export function MonthlyPLPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [downloading, setDownloading] = useState(false);
  const { data, isLoading } = useMonthlyPL(year, month);

  const handleCsv = async () => {
    setDownloading(true);
    try {
      const [from, to] = monthBounds(year, month);
      await downloadPLSummaryCsv(from, to);
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) return <Loader />;
  if (!data) return <Text c="dimmed">Veri yok</Text>;

  const trend = (data.daily_breakdown ?? []).map((d: any) => ({
    day: `${d.day}`,
    Ciro: d.revenue_minor / 100,
    Gider: d.expenses_minor / 100,
    "Net": d.net_profit_minor / 100,
  }));

  const years = Array.from({ length: 5 }, (_, i) => `${now.getFullYear() - i}`);

  return (
    <Stack gap="lg">
      <PageHeader
        title="📊 Aylık Kâr / Zarar"
        subtitle="Gelir vs tüm giderler (maaş + elektrik + tedarikçi + zayiat)"
      />

      <Group>
        <Select
          label="Yıl"
          value={`${year}`}
          onChange={(v) => v && setYear(Number(v))}
          data={years}
          w={120}
        />
        <Select
          label="Ay"
          value={`${month}`}
          onChange={(v) => v && setMonth(Number(v))}
          data={TR_MONTHS.map((name, i) => ({ value: `${i + 1}`, label: name }))}
          w={150}
        />
        <Button
          leftSection={<IconDownload size={16} />}
          variant="light"
          loading={downloading}
          onClick={handleCsv}
          mt={24}
        >
          CSV İndir
        </Button>
      </Group>

      {/* Ay Özet KPI */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <KPI label="Toplam Ciro" value={data.total_revenue_minor} color="green" />
        <KPI label="Toplam Gider" value={data.total_expenses_minor} color="red" />
        <KPI label="Toplam Zayiat" value={data.total_loss_minor} color="orange" />
        <KPI
          label="NET KÂR"
          value={data.net_profit_minor}
          color={data.net_profit_minor >= 0 ? "teal" : "red"}
          big
        />
      </SimpleGrid>

      {/* Gider Detayı */}
      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md" c="red">💸 Gider Kalemleri</Title>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          <Row label="Genel (Elektrik/Su/Kira)" v={data.expenses_total_minor} />
          <Row label="Tedarikçiye Ödenen" v={data.procurement_paid_minor} />
          <Row label="Maaş" v={data.payroll_paid_minor} />
          <Row label="Avans" v={data.advance_paid_minor} />
          <Row label="Personel Yemeği" v={data.staff_meal_cost_minor ?? 0} />
        </SimpleGrid>
        <Divider my="md" />
        <Group justify="space-between">
          <Text fw={700} size="lg">Gider Toplamı</Text>
          <Text fw={700} size="lg" c="red"><MoneyDisplay minor={data.total_expenses_minor} /></Text>
        </Group>
      </Paper>

      {/* Zayiat Detayı */}
      <Paper p="lg" radius="md" withBorder
        style={{ borderColor: data.total_loss_minor > 0 ? "#fa5252" : undefined }}>
        <Title order={4} mb="md" c="orange">🗑️ Zayiat Detayı</Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Row label="Fire (WasteRecord)" v={data.waste_total_minor} />
          <Row label="Sayım Düzeltme Kaybı" v={data.adjustment_loss_minor} />
        </SimpleGrid>
      </Paper>

      {/* İkram (Comp) — görünürlük; net kâra ek düşüm yok (bedel zaten ciroyu
          düşürdü). Food cost maliyeti olarak ayrı gösterilir. */}
      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md" c="pink">🎁 İkram (Comp) Maliyeti</Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Row label="Toplam İkram (food cost)" v={data.comp_total_minor ?? 0} />
        </SimpleGrid>
        <Text size="xs" c="dimmed" mt="xs">
          İkram = müşteriye gitti, ücret alınmadı. Bedel ciroyu düşürdüğü için
          net kâra ayrıca eklenmez — bu satır maliyet görünürlüğü içindir.
        </Text>
      </Paper>

      {/* Günlük Trend Grafiği */}
      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md">📈 Günlük Trend ({TR_MONTHS[month - 1]} {year})</Title>
        <AreaChart
          h={300}
          data={trend}
          dataKey="day"
          series={[
            { name: "Ciro", color: "green.6" },
            { name: "Gider", color: "red.6" },
            { name: "Net", color: "blue.6" },
          ]}
          curveType="natural"
        />
      </Paper>

      {/* Günlük Detay Tablosu */}
      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md">📋 Günlük Detay</Title>
        <Table.ScrollContainer minWidth={560}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Gün</Table.Th>
              <Table.Th ta="right">Sipariş</Table.Th>
              <Table.Th ta="right">Ciro</Table.Th>
              <Table.Th ta="right">Gider</Table.Th>
              <Table.Th ta="right">Zayiat</Table.Th>
              <Table.Th ta="right">Net</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.daily_breakdown?.map((d: any) => (
              <Table.Tr key={d.day}>
                <Table.Td>{d.day}</Table.Td>
                <Table.Td ta="right">{d.order_count}</Table.Td>
                <Table.Td ta="right" c="green"><MoneyDisplay minor={d.revenue_minor} /></Table.Td>
                <Table.Td ta="right" c="red"><MoneyDisplay minor={d.expenses_minor} /></Table.Td>
                <Table.Td ta="right" c="orange">
                  <MoneyDisplay minor={d.waste_minor + d.adjustment_loss_minor} />
                </Table.Td>
                <Table.Td ta="right" fw={700} c={d.net_profit_minor >= 0 ? "teal" : "red"}>
                  <MoneyDisplay minor={d.net_profit_minor} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}

function KPI({ label, value, color, big }: { label: string; value: number; color: string; big?: boolean }) {
  return (
    <Card padding="lg" radius="md" withBorder>
      <Text size="xs" c="dimmed" mb="xs">{label}</Text>
      <Title order={big ? 1 : 2} c={color}>
        <MoneyDisplay minor={value ?? 0} />
      </Title>
    </Card>
  );
}

function Row({ label, v }: { label: string; v: number }) {
  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={600}><MoneyDisplay minor={v ?? 0} /></Text>
    </Stack>
  );
}
